import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ItineraryItem } from '../types';

type DragHandleProps = {
  attributes?: Record<string, any>;
  listeners?: Record<string, any>;
};

interface ItineraryItemCardProps {
  item: ItineraryItem;
  isSelected?: boolean;
  onClick?: () => void;
  onEdit?: (item: ItineraryItem) => void;
  onDelete?: (item: ItineraryItem) => void;
  onMarkDone?: (item: ItineraryItem) => void;
  dragHandleProps?: DragHandleProps;
  isDragging?: boolean;
}

const ItineraryItemCard: React.FC<ItineraryItemCardProps> = ({ item, isSelected = false, onClick, onEdit, onDelete, onMarkDone, dragHandleProps, isDragging = false }) => {
  const [showDirectionsModal, setShowDirectionsModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  
  // Swipe to delete state
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  
  const DELETE_THRESHOLD = 80; // pixels to swipe before triggering delete
  const DONE_THRESHOLD = 80; // pixels to swipe before triggering mark as done
  const isDone = item.isDone ?? false;
  const getItemTypeIcon = (type: string) => {
    switch (type) {
      case 'activity':
        return '🎯';
      case 'restaurant':
        return '🍽️';
      case 'attraction':
        return '🏛️';
      case 'transportation':
        return '🚗';
      case 'lodging':
        return '🏨';
      case 'shopping':
        return '🛍️';
      case 'entertainment':
        return '🎭';
      default:
        return '📍';
    }
  };

  const formatCost = (costInCents: number) => {
    return `$${(costInCents / 100).toFixed(2)}`;
  };

  const getGoogleMapsUrl = () => {
    const placeQuery = encodeURIComponent(item.placeName);
    return `https://www.google.com/maps/search/?api=1&query=${placeQuery}`;
  };

  const getAppleMapsUrl = () => {
    const placeQuery = encodeURIComponent(item.placeName);
    return `http://maps.apple.com/?q=${placeQuery}`;
  };

  const openInMapsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    const ua = navigator.userAgent || '';
    const isIphone = /iPhone/.test(ua);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    // Desktop: always open Google Maps
    if (!isMobile) {
      window.open(getGoogleMapsUrl(), '_blank');
      return;
    }

    // Mobile iPhone: show choice modal
    if (isIphone) {
      setShowDirectionsModal(true);
      return;
    }

    // Android or other mobile: open Google Maps
    window.open(getGoogleMapsUrl(), '_blank');
  };

  const handleOpenGoogleMaps = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setShowDirectionsModal(false);
    window.open(getGoogleMapsUrl(), '_blank');
  };

  const handleOpenAppleMaps = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setShowDirectionsModal(false);
    window.open(getAppleMapsUrl(), '_blank');
  };

  const handleCopyAddress = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      const text = item.placeName;
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback prompt if clipboard API fails
      window.prompt('Copy address:', item.placeName);
    } finally {
      setShowDirectionsModal(false);
    }
  };

  // Check if description is actually being truncated by measuring the element
  useEffect(() => {
    if (!descriptionRef.current || !item.description || isExpanded) {
      setNeedsTruncation(false);
      return;
    }

    const checkTruncation = () => {
      const element = descriptionRef.current;
      if (!element) return;

      // Create a temporary clone to measure the full height without line-clamp
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.position = 'absolute';
      clone.style.visibility = 'hidden';
      clone.style.height = 'auto';
      clone.style.maxHeight = 'none';
      clone.style.display = 'block';
      clone.style.webkitLineClamp = 'none';
      clone.style.webkitBoxOrient = 'unset';
      clone.style.overflow = 'visible';
      clone.className = clone.className.replace('line-clamp-3', '');
      
      // Temporarily append to DOM to measure
      element.parentElement?.appendChild(clone);
      const fullHeight = clone.offsetHeight;
      element.parentElement?.removeChild(clone);

      // Get the clamped height
      const clampedHeight = element.offsetHeight;

      // If full height is greater than clamped height, text is truncated
      const isTruncated = fullHeight > clampedHeight;
      setNeedsTruncation(isTruncated);
    };

    // Use requestAnimationFrame to ensure the element is fully rendered
    const rafId = requestAnimationFrame(() => {
      setTimeout(checkTruncation, 0);
    });
    
    // Also check on window resize
    window.addEventListener('resize', checkTruncation);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [item.description, isExpanded]);

  // Detect if we're on a touch device (mobile/tablet)
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // Swipe/drag to delete and mark as done handlers - works on both mobile and desktop
  useEffect(() => {
    if (!cardRef.current) return;
    
    const cardElement = cardRef.current;
    let startX = 0;
    let startY = 0;
    let isSwipeActive = false;
    let currentOffset = 0;

    const handleStart = (clientX: number, clientY: number, target: HTMLElement) => {
      // Don't allow swipe if dragging
      if (isDragging) return;
      
      // Check if started on a button or interactive element
      if (target.closest('button') || target.closest('a')) return;
      
      startX = clientX;
      startY = clientY;
      swipeStartRef.current = { x: startX, y: startY };
      isSwipeActive = true;
      currentOffset = 0;
      setIsSwiping(true);
    };

    const handleMove = (clientX: number, clientY: number, e?: Event) => {
      if (!isSwipeActive || isDragging || !swipeStartRef.current) {
        if (!isSwipeActive) return;
        // Reset if dragging started
        swipeStartRef.current = null;
        isSwipeActive = false;
        setIsSwiping(false);
        currentOffset = 0;
        setSwipeOffset(0);
        return;
      }
      
      const deltaX = clientX - startX;
      const deltaY = Math.abs(clientY - startY);
      
      // Check if it's more horizontal than vertical
      const isHorizontal = Math.abs(deltaX) > deltaY * 1.5;
      
      // Require at least 10px horizontal movement to start swiping
      if (isHorizontal && Math.abs(deltaX) > 10) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        
        // Handle left swipe (negative deltaX) - delete
        if (deltaX < 0) {
          currentOffset = Math.max(deltaX, -DELETE_THRESHOLD * 1.5); // Cap at 1.5x threshold
          setSwipeOffset(currentOffset);
        } 
        // Handle right swipe (positive deltaX) - mark as done
        else if (deltaX > 0) {
          currentOffset = Math.min(deltaX, DONE_THRESHOLD * 1.5); // Cap at 1.5x threshold
          setSwipeOffset(currentOffset);
        }
      } else if (Math.abs(currentOffset) > 0) {
        // Allow swiping back if already swiped
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        // If swiped left, allow swiping back to the right
        if (currentOffset < 0 && deltaX > currentOffset) {
          currentOffset = Math.min(0, deltaX);
          setSwipeOffset(currentOffset);
        }
        // If swiped right, allow swiping back to the left
        else if (currentOffset > 0 && deltaX < currentOffset) {
          currentOffset = Math.max(0, deltaX);
          setSwipeOffset(currentOffset);
        }
      }
    };

    const handleEnd = () => {
      if (!isSwipeActive || !swipeStartRef.current || isDragging) {
        swipeStartRef.current = null;
        isSwipeActive = false;
        setIsSwiping(false);
        currentOffset = 0;
        setSwipeOffset(0);
        return;
      }

      // If swiped left past threshold, trigger delete (will show confirmation dialog)
      if (currentOffset <= -DELETE_THRESHOLD && onDelete) {
        onDelete(item);
      }
      // If swiped right past threshold, trigger mark as done
      else if (currentOffset >= DONE_THRESHOLD && onMarkDone) {
        onMarkDone(item);
      }
      
      // Reset swipe state with animation
      swipeStartRef.current = null;
      isSwipeActive = false;
      setIsSwiping(false);
      currentOffset = 0;
      setSwipeOffset(0);
    };

    // Touch event handlers (mobile)
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      handleStart(touch.clientX, touch.clientY, e.target as HTMLElement);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY, e);
    };

    // Mouse event handlers (desktop)
    const handleMouseDown = (e: MouseEvent) => {
      // Only handle left mouse button
      if (e.button !== 0) return;
      handleStart(e.clientX, e.clientY, e.target as HTMLElement);
    };

    const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY, e);
    };

    const handleMouseUp = () => {
      handleEnd();
    };

    // Add touch event listeners
    const handleTouchEnd = () => handleEnd();
    cardElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    cardElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    cardElement.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Add mouse event listeners
    cardElement.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      cardElement.removeEventListener('touchstart', handleTouchStart);
      cardElement.removeEventListener('touchmove', handleTouchMove);
      cardElement.removeEventListener('touchend', handleTouchEnd);
      cardElement.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onDelete, onMarkDone, item]);

  return (
    <div className="relative" style={{ overflow: 'visible' }}>
      {/* Delete background - shows when swiping left */}
      {swipeOffset < 0 && (
        <>
          <div 
            className="absolute inset-0 bg-[#E05A4D] rounded-2xl"
            style={{
              opacity: Math.min(1, Math.abs(swipeOffset) / DELETE_THRESHOLD),
              zIndex: 1,
              pointerEvents: 'none'
            }}
          />
          <div 
            className="absolute inset-0 flex items-center pointer-events-none"
            style={{
              zIndex: 3,
              opacity: Math.min(1, Math.abs(swipeOffset) / DELETE_THRESHOLD),
              justifyContent: 'flex-end',
              paddingRight: '40px'
            }}
          >
            <svg 
              width="32" 
              height="32" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" 
                stroke="white" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
        </>
      )}
      
      {/* Mark as done background - shows when swiping right */}
      {swipeOffset > 0 && (
        <>
          <div 
            className="absolute inset-0 bg-green-500 rounded-2xl"
            style={{
              opacity: Math.min(1, swipeOffset / DONE_THRESHOLD),
              zIndex: 1,
              pointerEvents: 'none'
            }}
          />
          <div 
            className="absolute inset-0 flex items-center pointer-events-none"
            style={{
              zIndex: 3,
              opacity: Math.min(1, swipeOffset / DONE_THRESHOLD),
              justifyContent: 'flex-start',
              paddingLeft: '40px'
            }}
          >
            <svg 
              width="32" 
              height="32" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M20 6L9 17l-5-5" 
                stroke="white" 
                strokeWidth="2.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
        </>
      )}
      
      <div 
        ref={cardRef}
        id={`item-${item.itemId}`}
        onClick={(e) => {
          // Only trigger onClick if not swiping, not dragging, and not clicking on interactive elements
          if (!isSwiping && !isDragging && swipeOffset === 0 && onClick) {
            const target = e.target as HTMLElement;
            // Don't trigger if clicking on buttons, links, or drag handle
            if (!target.closest('button') && !target.closest('a') && !target.closest('[data-drag-handle]')) {
              onClick();
            }
          }
        }}
        className={`rounded-2xl p-4 border shadow-[0_2px_6px_rgba(0,0,0,0.04)] transition-all duration-200 relative ${
          isSelected && !isDragging && !isDone
            ? 'border-primary-500 border-2 bg-primary-50/30'
            : 'border-[#E3E8E5]'
        } ${
          isDragging 
            ? 'bg-[#F2FBF9] opacity-90 scale-[0.99]' 
            : isDone
              ? 'bg-gray-300' 
            : isSelected && !isDone
              ? 'bg-primary-50/30'
              : 'bg-white'
        } ${isSwiping && !isDragging ? '' : 'transition-transform'} ${
          !isTouchDevice && !isDragging && swipeOffset === 0 ? 'hover:cursor-pointer' : ''
        }`}
        style={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          touchAction: isDragging ? 'none' : 'pan-y',
          WebkitTapHighlightColor: 'transparent',
          transform: swipeOffset !== 0 ? `translateX(${swipeOffset}px)` : 'translateX(0)',
          transition: isSwiping && !isDragging ? 'none' : 'transform 0.2s ease-out',
          zIndex: 2,
          position: 'relative',
          cursor: !isTouchDevice && !isDragging && swipeOffset === 0 ? (isSelected ? 'pointer' : 'pointer') : isDragging ? 'grabbing' : 'default'
        }}
        onMouseEnter={(e) => {
          // Show pointer cursor on desktop when entering card area
          if (!isTouchDevice && !isDragging && swipeOffset === 0) {
            const target = e.target as HTMLElement;
            if (!target.closest('button') && !target.closest('a') && !target.closest('[data-drag-handle]')) {
              (e.currentTarget as HTMLElement).style.cursor = 'pointer';
            }
          }
        }}
        onMouseMove={(e) => {
          // Update cursor based on what element is being hovered
          // Show pointer cursor on desktop to indicate card is clickable
          if (isTouchDevice || isDragging) return;
          
          const target = e.target as HTMLElement;
          const cardElement = e.currentTarget as HTMLElement;
          
          // Don't show pointer cursor when hovering over buttons, links, or the drag handle
          if (target.closest('button') || target.closest('a') || target.closest('[data-drag-handle]')) {
            // Let buttons/links show their own cursor (pointer)
            return;
          }
          
          // Show pointer cursor to indicate card is clickable
          if (swipeOffset === 0) {
            cardElement.style.cursor = 'pointer';
          }
        }}
      >
      {/* Reorder Button - Middle Right */}
      <button
        data-drag-handle
        onClick={(e) => {
          e.stopPropagation();
        }}
        className={`absolute top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center transition-colors cursor-grab active:cursor-grabbing rounded-full ${
          isDragging 
            ? 'bg-[#0B1412] text-white' 
            : 'text-[#6F7974] hover:text-[#0B1412] hover:bg-[#F2FBF9]'
        }`}
        title="Reorder"
        aria-label="Reorder"
        style={{
          right: '-16px',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent',
          zIndex: 20,
          position: 'absolute'
        }}
        {...(dragHandleProps?.attributes || {})}
        {...(dragHandleProps?.listeners || {})}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="18" r="1.5" />
        </svg>
      </button>
      {/* Header: Title and Icon */}
      <div className="flex items-start justify-between mb-3">
        <h3 
          className={`font-bold flex-1 ${isDone ? 'text-gray-600' : 'text-[#0B1412]'}`}
          style={{ fontSize: '20px', fontWeight: 700 }}
        >
          {item.name}
        </h3>
        {/* Item type icon */}
        <div className="w-8 h-8 flex items-center justify-center text-xl flex-shrink-0 ml-2">
          {getItemTypeIcon(item.itemType)}
        </div>
      </div>

      {/* Meta Tags: Time and Cost */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span 
          className={`inline-flex items-center px-3 py-1.5 rounded-2xl border ${
            isDone 
              ? 'border-gray-400 bg-gray-200 text-gray-600' 
              : 'border-[#E3E8E5] text-[#0B1412]'
          }`}
          style={{ fontSize: '13px', fontWeight: 600, height: '32px' }}
        >
          {item.startTime} - {item.endTime}
        </span>
        <span 
          className={`inline-flex items-center px-3 py-1.5 rounded-2xl border ${
            isDone 
              ? 'border-gray-400 bg-gray-200 text-gray-600' 
              : 'border-[#E3E8E5] text-[#0B1412]'
          }`}
          style={{ fontSize: '13px', fontWeight: 600, height: '32px' }}
        >
          {formatCost(item.estimatedCost)}
        </span>
      </div>

      {/* Description with Show More */}
      {item.description && (
        <div className="mb-3">
          <p 
            ref={descriptionRef}
            className={`leading-relaxed ${isExpanded ? '' : 'line-clamp-3'} ${isDone ? 'text-gray-500' : 'text-[#6F7974]'}`}
            style={{ 
              fontSize: '15px',
              lineHeight: '1.5'
            }}
          >
            {item.description}
          </p>
          {needsTruncation && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="text-primary-700 hover:text-primary-500 text-sm font-medium mt-1 transition-colors"
              style={{ fontSize: '15px' }}
            >
              {isExpanded ? 'Show Less' : 'Show More'}
            </button>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 flex-nowrap">
        {/* Edit Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit && onEdit(item);
          }}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium transition-colors flex-shrink-0 ${
            isDone
              ? 'border-gray-400 bg-gray-200 text-gray-600 hover:bg-gray-300'
              : 'border-[#E3E8E5] bg-white text-[#0B1412] hover:bg-[#F2FBF9]'
          }`}
          title="Edit"
          aria-label="Edit"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <span>Edit</span>
        </button>

        {/* Directions Button - Only if location or placeName exists */}
        {(item.location?.lat && item.location?.lon) || item.placeName ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              openInMapsApp(e);
            }}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium transition-colors flex-shrink-0 ${
              isDone
                ? 'border-gray-400 bg-gray-200 text-gray-600 hover:bg-gray-250'
                : 'border-[#E3E8E5] bg-white text-[#0B1412] hover:bg-[#F2FBF9]'
            }`}
            title="Directions"
            aria-label="Directions"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <span>Directions</span>
          </button>
        ) : null}

        {/* Website Button - Only if officialUrl exists */}
        {item.officialUrl && (
          <a
            href={item.officialUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium transition-colors flex-shrink-0 ${
              isDone
                ? 'border-gray-400 bg-gray-200 text-gray-600 hover:bg-gray-250'
                : 'border-[#E3E8E5] bg-white text-[#0B1412] hover:bg-[#F2FBF9]'
            }`}
            title="Website"
            aria-label="Website"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            <span>Website</span>
          </a>
        )}
      </div>
      </div>
      {showDirectionsModal && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => {
            e.stopPropagation();
            setShowDirectionsModal(false);
          }}
        >
          <div
            className="w-[92%] max-w-[420px] bg-white rounded-2xl shadow-lg p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <div className="flex flex-col gap-3">
              <button
                className="btn w-full focus:outline-none focus:ring-0 focus-visible:ring-0"
                onClick={handleOpenGoogleMaps}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Open in Google Maps
              </button>
              <button
                className="btn w-full focus:outline-none focus:ring-0 focus-visible:ring-0"
                onClick={handleOpenAppleMaps}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Open in Apple Maps
              </button>
              <button
                className="btn w-full focus:outline-none focus:ring-0 focus-visible:ring-0"
                onClick={handleCopyAddress}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Copy Address
              </button>
            </div>
            <button
              className="btn-ghost w-full mt-3 focus:outline-none focus:ring-0 focus-visible:ring-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowDirectionsModal(false);
              }}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ItineraryItemCard;
